/**
 * The Meta Pixel, for one job: telling the ad optimiser who made something.
 *
 * Without it Meta can only bid on clicks and landing-page views, and a click is
 * not a number worth bidding on (DEPLOY.md §8). So the pixel hears exactly
 * four things, the last three a ladder down Day 1:
 *
 *   page load                → PageView
 *   both intro questions     → IntroAnswered  (custom; a rung to read, not to bid on)
 *   first Day 1 start        → Lead   (warm-up, runner or the imagine starter, whichever is first)
 *   first Day 1 card made    → CompleteRegistration   ← the one to bid on
 *
 * The no-microphone starter (`?start=imagine`, screens/Imagine.tsx) climbs the
 * same ladder under its own event names: opening it is the start, and its post
 * landing is the card.
 *
 * Bid on the deepest rung that reaches about 50 a week per ad set, and if card
 * made is short of that, step up to Lead, not to IntroAnswered: two taps on big
 * buttons is the cheapest thing in the flow, and an optimiser told to find more
 * of it finds quiz-tappers (owner, 2026-10-13). It is a custom event for that
 * reason, so nobody mistakes it for a standard conversion in Ads Manager.
 *
 * "First" is per browser: a Day 4 card is not a new conversion, and counting it
 * would teach the optimiser to find people who were already here.
 *
 * What it never hears: anything anybody typed. Event names only, no params
 * beyond an id, and `autoConfig` off so the pixel does not scrape button text
 * and page metadata on its own — the app promises nobody sees anything unless
 * it is sent, and an ad network is somebody.
 *
 * PostHog stays the truth for everything after activation; this is the
 * platform's own signal, and the two will disagree.
 */

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const SENT_KEY = 'kindling.meta.sent';

type MetaEvent = 'IntroAnswered' | 'Lead' | 'CompleteRegistration';

/** Ours, not one of Meta's standard events: sent with `trackCustom`. */
const CUSTOM = new Set<MetaEvent>(['IntroAnswered']);

/** Kindling event → Meta standard event, sent once per browser. */
const ONCE: Record<string, MetaEvent> = {
  intro_answered: 'IntroAnswered',
  // Day 1 opens on the warm-up story and only then the runner; whichever comes
  // first is the start, and the second is swallowed by the once-flag.
  warmup_started: 'Lead',
  started: 'Lead',
  completed: 'CompleteRegistration',
  // Ending early still leaves a card behind; for activation it counts.
  ended_early: 'CompleteRegistration',
  // the imagine starter: opened is the start, its post kept is the card
  imagine_started: 'Lead',
  imagine_post_kept: 'CompleteRegistration',
};

type Fbq = ((...args: unknown[]) => void) & { callMethod?: (...a: unknown[]) => void; queue: unknown[]; loaded: boolean; version: string; push: unknown };
declare global { interface Window { fbq?: Fbq; _fbq?: Fbq } }

let ready = false;

function sent(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(SENT_KEY) || '[]') as string[]); } catch { return new Set(); }
}

function markSent(name: MetaEvent) {
  const s = sent();
  s.add(name);
  try { localStorage.setItem(SENT_KEY, JSON.stringify([...s])); } catch { /* sends again next time; harmless */ }
}

/**
 * An id per event, passed as `eventID`. Nothing reads it yet; it is what lets
 * the server-side Conversions API, when it comes, send the same event without
 * Meta counting it twice.
 */
function eventId(name: string): string {
  const rand = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${name}.${rand}`;
}

/** Meta's own loader, verbatim in behaviour: queue calls until fbevents.js arrives. */
function install(): Fbq {
  if (window.fbq) return window.fbq;
  const n = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod(...args); else n.queue.push(args);
  } as Fbq;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  window.fbq = n;
  window._fbq = n;
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);
  return n;
}

export function initMetaPixel(href = window.location.href) {
  if (!PIXEL_ID || ready || typeof window === 'undefined') return;
  // `?fresh` is "pretend I have never been here" (campaign.ts); the once-flags
  // go with it, or a tester could never fire a conversion twice.
  try {
    const q = new URL(href).searchParams;
    if (q.has('fresh') || q.has('newuser')) localStorage.removeItem(SENT_KEY);
  } catch { /* an unreadable url changes nothing */ }
  const fbq = install();
  fbq('set', 'autoConfig', false, PIXEL_ID);
  fbq('init', PIXEL_ID);
  fbq('track', 'PageView');
  ready = true;
}

/** Called from `track()` with every Kindling event; most are ignored. */
export function metaFromEvent(event: string) {
  const name = ONCE[event];
  if (!name || !ready || !window.fbq) return;
  if (sent().has(name)) return;
  window.fbq(CUSTOM.has(name) ? 'trackCustom' : 'track', name, {}, { eventID: eventId(name) });
  markSent(name);
}
